// types
import {
  FieldType,
  SystemProcessConditionAction,
  SystemProcessConditionType,
  SystemProcessObject,
  SystemProcessTransactionSendTo,
  SystemProcessTransactionType,
  SystemProcessType
} from 'types/graphql'
import { OBJECT_ENTITY_NAMES } from 'constants/objects'

export const FROM_PARENT_FIELD = 'FROM_PARENT_FIELD'

export const PROCESS_TYPE_OPTIONS = [
  {
    label: 'Trigger',
    value: SystemProcessType.Trigger
  },
  {
    label: 'Automation',
    value: SystemProcessType.Automation
  }
]

export const CONDITION_TYPE_OPTIONS = [
  {
    label: SystemProcessConditionType.Or,
    value: SystemProcessConditionType.Or
  },
  {
    label: SystemProcessConditionType.And,
    value: SystemProcessConditionType.And
  }
]

export const OBJECTS_OPTIONS = [
  {
    label: OBJECT_ENTITY_NAMES.ACTION,
    value: SystemProcessObject.Action
  },
  {
    label: OBJECT_ENTITY_NAMES.KPI,
    value: SystemProcessObject.Kpi
  },
  {
    label: OBJECT_ENTITY_NAMES.OBJECTIVE,
    value: SystemProcessObject.Objective
  },
  {
    label: OBJECT_ENTITY_NAMES.PLAN,
    value: SystemProcessObject.Plan
  }
]

export const CONDITION_ACTION_OPTIONS = [
  {
    label: 'Is',
    value: SystemProcessConditionAction.Is
  },
  {
    label: 'Is not',
    value: SystemProcessConditionAction.IsNot
  },
  {
    label: 'Has changed',
    value: SystemProcessConditionAction.HasChanged
  },
  {
    label: 'Contains ',
    value: SystemProcessConditionAction.Contains
  }
]

export const CONDITION_DATE_ACTION_OPTIONS = [
  {
    label: 'Days Until Due Date Is',
    value: SystemProcessConditionAction.DaysUntil
  },
  {
    label: 'Days Since Due Date Is',
    value: SystemProcessConditionAction.DaysSince
  },
  {
    label: 'Has changed',
    value: SystemProcessConditionAction.HasChanged
  }
]

export const CONDITION_NULL_OPTIONS = [
  {
    label: 'Is Not Null',
    value: SystemProcessConditionAction.IsNotNull
  },
  {
    label: 'Is Null',
    value: SystemProcessConditionAction.IsNull
  }
]

// as const — see the matching comment on FORM_SELECT_SECURITY_VALUES
// (constants/security.ts) for why this matters.
export const OBJECT_ACTIONS = {
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  COMPLETED: 'COMPLETED',
  REOPENED: 'REOPENED',
  APPROVED: 'APPROVED'
} as const

const SWITCH_FIELD_ACTIONS = {
  TRUE: true,
  FALSE: false
}

export const OBJECT_ACTIONS_OPTIONS = [
  {
    label: 'Created ',
    value: OBJECT_ACTIONS.CREATED
  },
  {
    label: 'Updated ',
    value: OBJECT_ACTIONS.UPDATED
  }
]

export const OBJECT_ACTIONS_EXTENDED_OPTIONS = [
  { label: 'Completed', value: OBJECT_ACTIONS.COMPLETED },
  { label: 'Reopened', value: OBJECT_ACTIONS.REOPENED }
]

export const REVIEW_OPTIONS = [
  { label: 'Created', value: OBJECT_ACTIONS.CREATED },
  { label: 'Completed', value: OBJECT_ACTIONS.COMPLETED },
  { label: 'Approved', value: OBJECT_ACTIONS.APPROVED }
]

export const SWITCH_FIELD_OPTIONS = [
  {
    label: 'True ',
    value: SWITCH_FIELD_ACTIONS.TRUE
  },
  {
    label: 'False ',
    value: SWITCH_FIELD_ACTIONS.FALSE
  }
]

export const TRANSACTION_TYPE_OPTIONS = [
  { label: 'Send Email', value: SystemProcessTransactionType.SendEmail },
  { label: 'Set Property', value: SystemProcessTransactionType.SetField },
  { label: 'Set Review Approver', value: SystemProcessTransactionType.SetReviewApprover }
]

export const RISK_TRANSACTION_TYPE_OPTIONS = [
  { label: 'Send Email', value: SystemProcessTransactionType.SendEmail },
  { label: 'Update Record', value: SystemProcessTransactionType.SetField },
  { label: 'Show Function', value: SystemProcessTransactionType.ShowFunction },
  { label: 'Show Inline Function', value: SystemProcessTransactionType.ShowInlineFunction },
  { label: 'Show Related Form', value: SystemProcessTransactionType.ShowRelatedForm },
  { label: 'Create Related Form', value: SystemProcessTransactionType.CreateRelatedForm },
  { label: 'Create Schedule', value: SystemProcessTransactionType.CreateRiskRecurrence },
  { label: 'Disable Schedule', value: SystemProcessTransactionType.DisableRiskRecurrence },
  { label: 'Enable Schedule', value: SystemProcessTransactionType.EnableRiskRecurrence },
  { label: 'Confirmation', value: SystemProcessTransactionType.Confirmation },
  { label: 'Response Message', value: SystemProcessTransactionType.ResponseMessage },
  { label: 'Check Mandatory', value: SystemProcessTransactionType.CheckMandatory },
  { label: 'Disable Edit', value: SystemProcessTransactionType.DisableUpdate },
  { label: 'Disable Delete', value: SystemProcessTransactionType.DisableDelete },
  { label: 'Restrict Record', value: SystemProcessTransactionType.Restrict },
  { label: 'Enable Group Access', value: SystemProcessTransactionType.EnableGroupAccess },
  { label: 'Enable Supervisor Hierarchy', value: SystemProcessTransactionType.EnableSupervisorHierarchy },
  { label: 'Make Private', value: SystemProcessTransactionType.RemoveGuestAccess },
  { label: 'Access From Related Form', value: SystemProcessTransactionType.AccessFromRelatedForm },
  { label: 'Injury Master: Create Case', value: SystemProcessTransactionType.CreateCase },
  { label: 'Injury Master: Update Case', value: SystemProcessTransactionType.UpdateCase },
  { label: 'Injury Master: Create Parent Case', value: SystemProcessTransactionType.CreateParentCase },
  { label: 'Injury Master: Update Parent Case', value: SystemProcessTransactionType.UpdateParentCase }
]

export const AUTOMATION_CONDITION_ACTION_OPTIONS = [
  {
    label: 'Is',
    value: SystemProcessConditionAction.Is
  }
]

export const AUTOMATION_PLAN_OBJECT_OPTIONS = [
  { label: 'Plan Review: Days until due date', value: 'planReviewDaysUntilDueDate', fieldType: FieldType.Json },
  { label: 'Plan Review: Days since due date', value: 'planReviewDaysSinceDueDate', fieldType: FieldType.Json },
  { label: 'Plan Review: Status', value: 'planReviewStatus', fieldType: FieldType.PickList }
]

export const AUTOMATION_OBJECTIVE_OBJECT_OPTIONS = [
  {
    label: 'Objective Review: Days until due date',
    value: 'objectiveReviewDaysUntilDueDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Objective Review: Days since due date',
    value: 'objectiveReviewDaysSinceDueDate',
    fieldType: FieldType.Json
  },
  { label: 'Objective Review: Status', value: 'objectiveReviewStatus', fieldType: FieldType.PickList },
  {
    label: 'Objective: Days until Planned Completion Date',
    value: 'objectiveDaysUntilPlannedCompletionDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Objective: Days since Planned Completion Date',
    value: 'objectiveDaysSincePlannedCompletionDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Objective: Days until Planned Commencement Date',
    value: 'objectiveDaysUntilPlannedCommencementDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Objective: Days since Planned Commencement Date',
    value: 'objectiveDaysSincePlannedCommencementDate',
    fieldType: FieldType.Json
  },
  { label: 'Objective: Completion Percentage', value: 'completionPercentage', fieldType: FieldType.Number }
]

export const AUTOMATION_ACTION_OBJECT_OPTIONS = [
  { label: 'Action Review: Days until due date', value: 'actionReviewDaysUntilDueDate', fieldType: FieldType.Json },
  { label: 'Action Review: Days since due date', value: 'actionReviewDaysSinceDueDate', fieldType: FieldType.Json },
  { label: 'Action Review: Status', value: 'actionReviewStatus', fieldType: FieldType.PickList },
  {
    label: 'Action: Days until Planned Completion Date',
    value: 'actionDaysUntilPlannedCompletionDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Action: Days since Planned Completion Date',
    value: 'actionDaysSincePlannedCompletionDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Action: Days until Planned Commencement Date',
    value: 'actionDaysUntilPlannedCommencementDate',
    fieldType: FieldType.Json
  },
  {
    label: 'Action: Days since Planned Commencement Date',
    value: 'actionDaysSincePlannedCommencementDate',
    fieldType: FieldType.Json
  },
  { label: 'Action: Completion Percentage', value: 'completionPercentage', fieldType: FieldType.Number }
]

export const AUTOMATION_KPI_OBJECT_OPTIONS = [
  { label: 'Indicator Review: Days until due date', value: 'kpiReviewDaysUntilDueDate', fieldType: FieldType.Json },
  { label: 'Indicator Review: Days since due date', value: 'kpiReviewDaysSinceDueDate', fieldType: FieldType.Json },
  { label: 'Indicator Review: Status', value: 'kpiReviewStatus', fieldType: FieldType.PickList },
  { label: 'Indicator Result: Days until due date', value: 'kpiResultDaysUntilDueDate', fieldType: FieldType.Json },
  { label: 'Indicator Result: Days since due date', value: 'kpiResultDaysSinceDueDate', fieldType: FieldType.Json }
]

export const RECIPIENT_GROUPING_TYPE = {
  USER: 'USER',
  GROUP: 'GROUP'
}

export const RECIPIENT_GROUPING_TYPE_OPTIONS = [{ label: 'to user', value: RECIPIENT_GROUPING_TYPE.USER }]

export const RECIPIENT_ENTITY_TYPE = {
  GROUP: 'GROUP',
  POSITION: 'POSITION',
  USER: 'USER',
  FIELD: 'FIELD',
  FIELD_TO_PREVIOUS: 'FIELD_TO_PREVIOUS',
  FIELD_TO_POSITION: 'FIELD_TO_POSITION',
  FIELD_TO_USER: 'FIELD_TO_USER',
  FIELD_TO_PARENT: 'FIELD_TO_PARENT'
}

export const RECIPIENT_TYPE_OPTIONS = [
  { label: '(assignee)', value: SystemProcessTransactionSendTo.Assignee, entity: RECIPIENT_ENTITY_TYPE.GROUP },
  { label: '(members)', value: SystemProcessTransactionSendTo.Members, entity: RECIPIENT_ENTITY_TYPE.GROUP },
  {
    label: '(reports to person)',
    value: SystemProcessTransactionSendTo.ReportsToPerson,
    entity: RECIPIENT_ENTITY_TYPE.GROUP
  },
  {
    label: '(reports to position)',
    value: SystemProcessTransactionSendTo.ReportsToPosition,
    entity: RECIPIENT_ENTITY_TYPE.GROUP
  },
  {
    label: '(reviewer)',
    value: SystemProcessTransactionSendTo.Reviewer,
    entity: RECIPIENT_ENTITY_TYPE.GROUP
  },
  {
    label: '(review approver)',
    value: SystemProcessTransactionSendTo.ReviewApprover,
    entity: RECIPIENT_ENTITY_TYPE.GROUP
  }
]

export const PLAN_METATAGS = [
  { label: 'URL', value: '{{plan.url}}' },
  { label: 'Title', value: '{{plan.name}}' },
  { label: 'Plan Type', value: '{{plan.planType.description}}' },
  { label: 'Owned By Firstname', value: '{{plan.ownedByUser.firstName}}' },
  { label: 'Owned By Surname', value: '{{plan.ownedByUser.lastName}}' },
  { label: 'Owned By Position', value: '{{plan.ownedByPosition.description}}' },
  { label: 'Status', value: '{{plan.status}}' }
]

export const OBJECTIVE_METATAGS = [
  { label: 'URL', value: '{{objective.url}}' },
  { label: 'Description', value: '{{objective.description}}' },
  { label: 'Owned By Firstname', value: '{{objective.ownedByUser.firstName}}' },
  { label: 'Owned By Surname', value: '{{objective.ownedByUser.lastName}}' },
  { label: 'Owned By Position', value: '{{objective.ownedByPosition.description}}' },
  { label: 'Plan Type', value: '{{plan.planType.description}}' }
]

export const ACTIONS_METATAGS = [
  { label: 'URL', value: '{{action.url}}' },
  { label: 'Is this is a milestone', value: '{{action.milestone}}' },
  { label: 'Description', value: '{{action.description}}' },
  { label: 'Status', value: '{{action.status}}' },
  { label: 'Owned By Firstname', value: '{{action.ownedByUser.firstName}}' },
  { label: 'Owned By Surname', value: '{{action.ownedByUser.lastName}}' },
  { label: 'Owned By Position', value: '{{action.ownedByPosition.description}}' },
  { label: 'Planned Completion Date Time', value: '{{action.completionAt}}' },
  { label: 'Planned Commencement Date Time', value: '{{action.commencementAt}}' },
  { label: 'Percentage', value: '{{action.completionPercentage}}' },
  { label: 'Plan Type', value: '{{plan.planType.description}}' }
]

export const KPI_METATAGS = [
  { label: 'URL', value: '{{kpi.url}}' },
  { label: 'Status', value: '{{kpi.status}}' },
  { label: 'Description', value: '{{kpi.description}}' },
  { label: 'Owned By Firstname', value: '{{kpi.ownedByUser.firstName}}' },
  { label: 'Owned By Surname', value: '{{kpi.ownedByUser.lastName}}' },
  { label: 'Owned By Position', value: '{{kpi.ownedByPosition.description}}' },
  { label: 'Input Method', value: '{{kpi.inputMethod}}' },
  { label: 'Unit of Measure', value: '{{kpi.unit}}' },
  { label: 'Result Input Type', value: '{{kpi.resultType}}' },
  { label: 'Result Baseline', value: '{{kpi.resultBaseline}}' },
  { label: 'Plan Type', value: '{{plan.planType.description}}' }
]

export const RISK_TEMPLATE_METATAGS = [
  { label: 'URL', value: '{{riskTemplate.url}}' },
  { label: 'Title', value: '{{riskTemplate.name}}' },
  { label: 'Uniq Id', value: '{{riskTemplate.uniqId}}' },
  { label: 'Created At', value: '{{riskTemplate.createdAt}}' }
] as const

export const PARENT_RISK_TEMPLATE_METATAGS = [
  { label: 'URL', value: '{{parentRiskTemplate.url}}' },
  { label: 'Title', value: '{{parentRiskTemplate.name}}' },
  { label: 'Uniq Id', value: '{{parentRiskTemplate.uniqId}}' },
  { label: 'Created At', value: '{{parentRiskTemplate.createdAt}}' }
] as const

export const FORM_MULTIPLE_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
]

export const FORM_POSITION_OPTIONS = new Array(100).fill({}).map((i, idx) => ({ label: idx + 1, value: idx + 1 }))
